'use client';

import React from 'react';

import he from 'he';
import parse from 'html-react-parser';
import sanitizeHtml from 'sanitize-html';
import styled from 'styled-components';

import { allowedTags } from './constants';
import { options } from './replace';
import EditInAdminButton from '../EditInAdminButton';

import 'video-react/dist/video-react.css';

const Wrapper = styled.div`
  position: relative;
  max-width: 100%;
  width: 100% !important;

  img,
  iframe {
    max-width: 100%;
  }

  iframe {
    border: 0;
  }
`;

interface ParsedContentProps {
  article?: ArticleData;
  content?: string;
}

export default function ParsedContent({
  article = null,
  content = '',
}: ParsedContentProps) {
  const [isHovered, setIsHovered] = React.useState(false);

  // Decode HTML entities first
  let decodedContent = '';
  if (content) {
    decodedContent = content ? he.decode(content) : '';
  }
  if (article) {
    decodedContent = article.content ? he.decode(article.content) : '';
  }

  // Comprehensive attribute mapping for JSX camelCase
  const attributeMap = {
    // Standard HTML attributes requiring camelCase
    acceptcharset: 'acceptCharset',
    accesskey: 'accessKey',
    autocomplete: 'autoComplete',
    autofocus: 'autoFocus',
    autoplay: 'autoPlay', // For <video> and <audio>
    autosave: 'autoSave',
    bgcolor: 'bgColor',
    cellpadding: 'cellPadding',
    cellspacing: 'cellSpacing',
    charset: 'charSet',
    class: 'className',
    classid: 'classID',
    colspan: 'colSpan',
    contenteditable: 'contentEditable',
    contextmenu: 'contextMenu',
    crossorigin: 'crossOrigin',
    datetime: 'dateTime',
    enctype: 'encType',
    for: 'htmlFor',
    formaction: 'formAction',
    formenctype: 'formEncType',
    formmethod: 'formMethod',
    formnovalidate: 'formNoValidate',
    formtarget: 'formTarget',
    frameborder: 'frameBorder',
    hreflang: 'hrefLang',
    inputmode: 'inputMode',
    longdesc: 'longDesc',
    marginheight: 'marginHeight',
    marginwidth: 'marginWidth',
    maxlength: 'maxLength',
    mediagroup: 'mediaGroup',
    minlength: 'minLength',
    novalidate: 'noValidate',
    playsinline: 'playsInline', // For <video> and <audio>
    readonly: 'readOnly',
    rowspan: 'rowSpan',
    spellcheck: 'spellCheck',
    srcdoc: 'srcDoc',
    srclang: 'srcLang',
    srcset: 'srcSet',
    tabindex: 'tabIndex',
    usemap: 'useMap',
    allowfullscreen: 'allowFullScreen',
    allowpaymentrequest: 'allowPaymentRequest',
    allowtransparency: 'allowTransparency',
    allowusermedia: 'allowUserMedia',
    lowsrc: 'lowSrc',
    // SVG-specific attributes requiring camelCase
    clippath: 'clipPath',
    fillopacity: 'fillOpacity',
    fontfamily: 'fontFamily',
    fontsize: 'fontSize',
    fontweight: 'fontWeight',
    gradienttransform: 'gradientTransform',
    gradientunits: 'gradientUnits',
    markerend: 'markerEnd',
    markerheight: 'markerHeight',
    markermid: 'markerMid',
    markerstart: 'markerStart',
    markerunits: 'markerUnits',
    markerwidth: 'markerWidth',
    maskcontentunits: 'maskContentUnits',
    maskunits: 'maskUnits',
    patterncontentunits: 'patternContentUnits',
    patternunits: 'patternUnits',
    preserveaspectratio: 'preserveAspectRatio',
    spreadmethod: 'spreadMethod',
    stopcolor: 'stopColor',
    stopopacity: 'stopOpacity',
    strokeopacity: 'strokeOpacity',
    strokewidth: 'strokeWidth',
    strokelinecap: 'strokeLinecap',
    strokelinejoin: 'strokeLinejoin',
    textanchor: 'textAnchor',
    textdecoration: 'textDecoration',
    viewbox: 'viewBox',
    xlinkactuate: 'xlinkActuate',
    xlinkarcrole: 'xlinkArcrole',
    xlinkhref: 'xlinkHref',
    xlinkrole: 'xlinkRole',
    xlinkshow: 'xlinkShow',
    xlinktitle: 'xlinkTitle',
    xlinktype: 'xlinkType',
    xmlbase: 'xmlBase',
    xmllang: 'xmlLang',
    xmlspace: 'xmlSpace',
  };

  const sanitizedContent = sanitizeHtml(decodedContent, {
    allowedTags,
    allowedAttributes: false,
    allowedStyles: false,
    allowVulnerableTags: false,
    transformTags: {
      '*': (tagName, attribs) => {
        const newAttribs = { ...attribs };
        Object.keys(newAttribs).forEach(key => {
          if (attributeMap[key]) {
            newAttribs[attributeMap[key]] = newAttribs[key];
            delete newAttribs[key];
          }
        });
        return { tagName, attribs: newAttribs };
      },
    },
  });

  const parsedContent = parse(sanitizedContent, options());

  return (
    <Wrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <EditInAdminButton item={article} visible={isHovered} />
      {parsedContent}
    </Wrapper>
  );
}
